/* eslint-disable react/prop-types */
import {
  UilPlus,
  UilTrash,
  UilPen,
  UilCheck,
  UilTimes,
  UilUpload,
  UilImport,
  UilCopy,
} from '@iconscout/react-unicons';
import {
  Box,
  Button,
  ButtonGroup,
  ConfirmDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Notification,
  TextField,
  Typography,
  Stack,
  Tooltip,
} from '@xbotvn/mui';
import {
  get,
  values,
  keyBy,
  replace,
  toLower,
} from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import { onlineBookApi } from '../../../api';
import ApprovalStatusChip from '../../../components/ApprovalStatusChip';
import DropzoneModal from '../../../components/DropzoneModal';
import Table from '../../../components/Table';
import Waiting from '../../../components/Waiting';
import { ONLINE_BOOK } from '../../../libs/config';
import { APPROVAL_OPTIONS, getThumbnail, normalizeText } from '../../../libs/utils';
import {
  FILE,
  ONLINEBOOK,
} from '../../../redux/actions/constants';
import { importExcel } from '../Helpers';

import Editor from './Editor';

const StatusFilter = ({
  column: {
    filterValue,
    setFilter,
  },
}) => (
  <TextField
    select
    SelectProps={{
      native: true,
    }}
    value={filterValue || ''}
    onChange={(event) => setFilter(event.target.value || undefined)}
  >
    <option value="">Tất cả</option>
    {APPROVAL_OPTIONS.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </TextField>
);

export default function OnlineBooksAdmin() {
  const dispatch = useDispatch();
  const {
    handling,
    onlineBooks,
    activeUnit,
    unitInfo,
    units,
  } = useSelector(({
    onlineBook,
    user,
    unit,
  }) => ({
    handling: get(onlineBook, 'handling', false),
    onlineBooks: onlineBook.data,
    activeUnit: user.activeUnit,
    unitInfo: unit.data?.[user?.activeUnit],
    units: unit.data,
  }));

  const [removeConfirm, setRemoveConfirm] = useState();
  const [editor, setEditor] = useState();
  const [sendConfirm, setSendConfirm] = useState();
  const [reviewDialog, setReviewDialog] = useState();
  const [pendingBooks, setPendingBooks] = useState([]);
  const [sharedBooks, setSharedBooks] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingShared, setLoadingShared] = useState(false);
  const [viewMode, setViewMode] = useState('unit');
  const [showImport, setShowImport] = useState(false);

  const hasChildren = useMemo(
    () => values(units || {}).some((unit) => unit.parentId === activeUnit),
    [units, activeUnit],
  );

  const hasParent = !!unitInfo?.parentId;

  const fetchPendingBooks = async () => {
    if (!activeUnit || !hasChildren) return;
    setLoadingPending(true);
    try {
      const {
        data,
      } = await onlineBookApi.getPending({
        data: {
          unitId: activeUnit,
        },
      });
      setPendingBooks(data.records || []);
    } catch (error) {
      Notification.error('Không thể lấy danh sách chờ duyệt');
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchSharedBooks = async () => {
    if (!activeUnit || !hasParent) return;
    setLoadingShared(true);
    try {
      const {
        data,
      } = await onlineBookApi.getShared({
        data: {
          unitId: activeUnit,
        },
      });
      setSharedBooks(data.records || []);
    } catch (error) {
      Notification.error('Không thể lấy sách chia sẻ');
    } finally {
      setLoadingShared(false);
    }
  };

  useEffect(() => {
    fetchPendingBooks();
  }, [activeUnit, hasChildren]);

  useEffect(() => {
    fetchSharedBooks();
  }, [activeUnit, hasParent]);

  const handleSendBook = async (bookId) => {
    try {
      const {
        data,
      } = await onlineBookApi.submit({
        _id: bookId,
        unitId: activeUnit,
      });
      if (data?.record?._id) {
        dispatch({
          type: ONLINEBOOK.update,
          data: {
            [data.record._id]: data.record,
          },
          merge: true,
        });
      }
      Notification.success('Đã gửi sách để xét duyệt');
    } catch (error) {
      Notification.error('Gửi sách thất bại');
    }
  };

  const handleReviewBook = async ({
    bookId,
    action,
    note,
    shareToChildren,
  }) => {
    try {
      const isChildBook = !!keyBy(pendingBooks, '_id')?.[bookId]?.subject;
      const {
        data,
      } = await onlineBookApi.review({
        _id: bookId,
        unitId: activeUnit,
        action,
        note,
        shareToChildren,
        isChildBook,
      });
      if (data?.approvedRecord?._id || (isChildBook && data?.newData)) {
        const dataUpdate = isChildBook
          ? { [data.newData._id]: data.newData }
          : {
            [data.approvedRecord._id]: data.approvedRecord,
            ...(data.newData?._id ? { [data.newData._id]: data.newData } : {}),
          };
        dispatch({
          type: ONLINEBOOK.update,
          data: dataUpdate,
          merge: true,
        });
      }
      setPendingBooks((prev) => prev.filter((item) => item._id !== bookId));
      Notification.success(action === 'approve' ? 'Đã duyệt sách' : 'Đã từ chối sách');
    } catch (error) {
      Notification.error('Xét duyệt thất bại');
    }
  };

  const unitBookList = useMemo(() => values(onlineBooks || {}), [onlineBooks]);
  const sharedBookList = useMemo(() => sharedBooks, [sharedBooks]);
  const combinedBooks = useMemo(() => {
    if (!sharedBookList.length) return unitBookList;
    const merged = {
      ...keyBy(unitBookList, '_id'),
      ...keyBy(sharedBookList, '_id'),
    };
    return values(merged);
  }, [unitBookList, sharedBookList]);

  const dataSource = useMemo(() => {
    switch (viewMode) {
      case 'pending':
        return pendingBooks;
      case 'shared':
        return sharedBookList;
      case 'combined':
        return combinedBooks;
      default:
        return unitBookList;
    }
  }, [viewMode, pendingBooks, sharedBookList, combinedBooks, unitBookList]);

  const centerCellStyle = {
    verticalAlign: 'middle',
  };

  const columns = useMemo(() => ([
    {
      Header: 'STT',
      id: 'stt',
      disableFilters: true,
      style: {
        width: 50,
        minWidth: 50,
        textAlign: 'center',
        ...centerCellStyle,
      },
      Cell: ({ row }) => (
        <div style={{ textAlign: 'center' }}>{row.index + 1}</div>
      ),
    },
    {
      Header: 'Nhan đề',
      accessor: 'name',
      style: {
        width: 300,
        minWidth: 200,
        ...centerCellStyle,
      },
    },
    {
      Header: 'Ảnh bìa',
      accessor: 'coverLink',
      style: {
        width: 120,
        minWidth: 100,
        textAlign: 'center',
        ...centerCellStyle,
      },
      disableFilters: true,
      Cell: ({ value }) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img
            src={getThumbnail(value)}
            alt="bìa"
            style={{ height: 60, maxWidth: '100%', objectFit: 'contain' }}
            onError={(e) => { e.target.src = '/defaultBook.jpg'; }}
          />
        </Box>
      ),
    },
    {
      Header: 'Link sách',
      accessor: 'bookLink',
      style: {
        width: 300,
        minWidth: 200,
        ...centerCellStyle,
      },
      disableFilters: true,
      Cell: ({ value }) => {
        if (!value) return '';
        const displayValue = value.length > 60 ? `${value.slice(0, 60)}...` : value;
        return (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            title={value}
            style={{
              color: '#1b76d2',
              textDecoration: 'underline',
              display: 'inline-block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayValue}
          </a>
        );
      },
    },
    ...(hasParent ? [{
      Header: 'Trạng thái',
      accessor: 'approvalStatus',
      Filter: StatusFilter,
      Cell: (rowProps) => {
        const {
          value,
          row,
        } = rowProps;
        const approvalNote = row?.original?.approvalNote;
        return (
          <Box display="flex" justifyContent="center">
            <ApprovalStatusChip status={value} note={approvalNote} />
          </Box>
        );
      },
      style: {
        width: 150,
        minWidth: 120,
        textAlign: 'center',
        ...centerCellStyle,
      },
    }] : []),
    ...(viewMode === 'pending' ? [{
      Header: 'Đơn vị gửi',
      accessor: 'unitId',
      style: {
        width: 220,
        minWidth: 180,
        ...centerCellStyle,
      },
      Cell: (rowProps) => units?.[rowProps?.value]?.name || '',
    }] : []),
    ...(hasParent ? [{
      Header: 'Gửi duyệt',
      id: 'send',
      disableFilters: true,
      style: {
        width: 100,
        textAlign: 'center',
        ...centerCellStyle,
      },
      Cell: (rowProps) => {
        const rowValue = get(rowProps, 'row.original', {});
        if (viewMode === 'shared' || viewMode === 'combined' || viewMode === 'pending') {
          return null;
        }

        const status = rowValue?.approvalStatus || 'draft';
        const canSend = status === 'draft' || status === 'rejected';

        if (canSend) {
          return (
            <Box display="flex" justifyContent="center">
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<UilUpload />}
                onClick={() => setSendConfirm(rowValue)}
              >
                Gửi
              </Button>
            </Box>
          );
        }
        return null;
      },
    }] : []),
    {
      Header: () => (viewMode === 'unit' ? (
        <Button
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setEditor({
            name: '',
            coverLink: '',
            bookLink: '',
            positionLink: '',
            unitId: activeUnit,
            isPublic: false,
            shareToChildren: true,
            approvalStatus: 'draft',
          })}
          startIcon={<UilPlus />}
        >
          Thêm
        </Button>
      ) : 'Thao tác'),
      id: 'actions',
      disableFilters: true,
      style: {
        width: 120,
        textAlign: 'center',
        ...centerCellStyle,
      },
      Cell: (rowProps) => {
        const rowValue = get(rowProps, 'row.original', {});
        if (viewMode === 'pending') {
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setReviewDialog({
                book: rowValue,
                note: '',
                shareToChildren: true,
              })}
              startIcon={<UilCheck />}
            >
              Xét duyệt
            </Button>
          );
        }

        if (viewMode === 'shared' || viewMode === 'combined') {
          return null;
        }

        return (
          <Box display="flex" justifyContent="center">
            <ButtonGroup>
              <IconButton
                onClick={() => setEditor(rowValue)}
                icon="edit"
                size="small"
                color="primary"
                sx={{
                  marginRight: 0.5,
                }}
              >
                <UilPen />
              </IconButton>
              <Divider orientation="vertical" flexItem />
              <IconButton
                color="error"
                size="small"
                variant="outlined"
                onClick={() => setRemoveConfirm(rowValue._id)}
                sx={{
                  marginLeft: 0.5,
                }}
              >
                <UilTrash />
              </IconButton>
            </ButtonGroup>
          </Box>
        );
      },
    },
  ]), [viewMode, units, activeUnit]);

  const editModal = useMemo(() => (
    <Editor
      onClose={() => setEditor()}
      onHandler={(params) => {
        dispatch({
          type: ONLINEBOOK.handlers.update,
          data: params,
          onSuccess: () => {
            setEditor();
          },
        });
      }}
      data={editor}
    />
  ), [editor]);

  const renderRemoveConfirm = useMemo(() => (
    <ConfirmDialog
      title="Bạn có chắc chắn muốn xóa?"
      color="error"
      confirmLabel="Xoá"
      cancelLabel="Huỷ"
      maxWidth="sm"
      fullWidth
      open
      onClose={() => setRemoveConfirm()}
      onConfirm={() => {
        dispatch({
          type: ONLINEBOOK.handlers.remove,
          _id: removeConfirm,
          onSuccess: () => {
            setRemoveConfirm();
          },
        });
      }}
    />
  ));

  const renderSendConfirm = () => (
    <ConfirmDialog
      title="Gửi sách để xét duyệt?"
      confirmLabel="Gửi"
      cancelLabel="Huỷ"
      maxWidth="sm"
      fullWidth
      open
      onClose={() => setSendConfirm()}
      onConfirm={async () => {
        await handleSendBook(sendConfirm?._id);
        setSendConfirm();
      }}
    />
  );

  const renderReviewDialog = () => (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      onClose={() => setReviewDialog()}
    >
      <DialogTitle onClose={() => setReviewDialog()}>
        Xét duyệt sách trực tuyến
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            label="Ghi chú"
            multiline
            minRows={3}
            value={reviewDialog?.note || ''}
            onChange={(event) => setReviewDialog((prev) => ({
              ...prev,
              note: event.target.value,
            }))}
          />
          <TextField
            select
            SelectProps={{
              native: true,
            }}
            label="Chia sẻ cho trường trực thuộc"
            value={reviewDialog?.shareToChildren ? 'yes' : 'no'}
            onChange={(event) => setReviewDialog((prev) => ({
              ...prev,
              shareToChildren: event.target.value === 'yes',
            }))}
          >
            <option value="yes">Có</option>
            <option value="no">Không</option>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color="success"
          startIcon={<UilCheck />}
          onClick={() => {
            handleReviewBook({
              bookId: reviewDialog?.book?._id,
              action: 'approve',
              note: reviewDialog?.note,
              shareToChildren: reviewDialog?.shareToChildren,
            });
            setReviewDialog();
          }}
        >
          Duyệt
        </Button>
        <Button
          color="error"
          startIcon={<UilTimes />}
          onClick={() => {
            handleReviewBook({
              bookId: reviewDialog?.book?._id,
              action: 'reject',
              note: reviewDialog?.note,
              shareToChildren: reviewDialog?.shareToChildren,
            });
            setReviewDialog();
          }}
        >
          Không duyệt
        </Button>
      </DialogActions>
    </Dialog>
  );

  const viewOptions = useMemo(() => ([
    {
      label: 'Sách của đơn vị',
      value: 'unit',
    },
    ...(hasChildren ? [{
      label: 'Sách chờ duyệt',
      value: 'pending',
    }] : []),
    ...(hasParent ? [{
      label: 'Sách từ thư viện cụm/sở',
      value: 'shared',
    },
    {
      label: 'Gộp sách của đơn vị + cụm/sở',
      value: 'combined',
    }] : []),
  ]), [hasChildren, hasParent]);

  const isLoading = handling || (viewMode === 'pending' && loadingPending)
    || ((viewMode === 'shared' || viewMode === 'combined') && loadingShared);

  const normalizeStr = replace(normalizeText(toLower(unitInfo?.name || '')), / /g, '_');
  const publicLink = `${window.location.origin}/sachTrucTuyen/${normalizeStr}/${activeUnit}`;

  return (
    <Box>
      {removeConfirm && renderRemoveConfirm}
      {editor ? editModal : null}
      {sendConfirm ? renderSendConfirm() : null}
      {reviewDialog ? renderReviewDialog() : null}
      {isLoading ? <Waiting /> : null}
      {showImport && (
      <DropzoneModal
        onConfirm={(files) => {
          if (!files.length) return;
          importExcel({
            files,
            activeUnit,
            config: ONLINE_BOOK,
            onSuccess: (records) => {
              if (records.some(({ errors }) => errors)) {
                const errorStr = records.find((rec) => rec.errors)?.errors || '';
                Notification.error(errorStr);
              } else {
                dispatch({
                  type: ONLINEBOOK.handlers.import,
                  data: records.map((record) => ({
                    ...record,
                    positionLink: '',
                    shareToChildren: true,
                    approvalStatus: 'draft',
                  })),
                });
                setShowImport();
              }
            },
          });
        }}
        onClose={() => setShowImport()}
      />
      )}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1,
      }}
      >
        <Button
          startIcon={<UilUpload />}
          color="success"
          size="small"
          onClick={() => setShowImport(true)}
        >
          Nhập từ Excel
        </Button>
        <Button
          startIcon={<UilImport />}
          color="primary"
          size="small"
          onClick={() => dispatch({
            type: FILE.handlers.download,
            data: {
              unitId: activeUnit,
              fileName: 'Mau_SachTrucTuyen.xlsx',
            },
          })}
        >
          Tải file mẫu
        </Button>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto',
        }}
        >
          <Tooltip title={publicLink} placement="top">
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => window.open(publicLink, '_blank')}
            >
              Link công khai
            </Button>
          </Tooltip>
          <Tooltip title="Sao chép link công khai" placement="top">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                navigator.clipboard.writeText(publicLink);
                Notification.success('Đã sao chép link');
              }}
            >
              <UilCopy />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
        <TextField
          select
          SelectProps={{
            native: true,
          }}
          label="Nguồn dữ liệu"
          value={viewMode}
          onChange={(event) => setViewMode(event.target.value)}
          sx={{ width: 260 }}
        >
          {viewOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 1 }}
      >
        *Đưa con trỏ chuột vào trạng thái để xem nhận xét từ cấp quản lý nếu có
      </Typography>
      <Table
        columns={columns}
        data={dataSource}
        disableGlobalFilter
        alwaysShowPagination
      />
    </Box>
  );
}

StatusFilter.propTypes = {
  column: PropTypes.shape({
    filterValue: PropTypes.string,
    setFilter: PropTypes.func.isRequired,
  }).isRequired,
};
